import { cn } from '@/lib/utils';
import { SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import axios from 'axios';
import { Search } from 'lucide-react';
import { ChangeEvent, ComponentProps, FormEvent, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import AppLogo from './app-logo';
import Link from './link';

function NavLink({
    href,
    component,
    inertia = false,
    prefetch = true,
    children,
    className,
}: PropsWithChildren<{ component?: string } & ComponentProps<typeof Link>>) {
    const activeComponent = usePage().props.activeComponent;

    return (
        <Link
            href={href}
            className={cn(
                'text-foreground rounded-sm px-2 py-1 hover:bg-[#a50000] hover:text-white 2xl:px-3 2xl:py-2',
                {
                    'bg-[#a50000] text-white': activeComponent && activeComponent === component,
                },
                className,
            )}
            inertia={inertia}
            {...(inertia && { prefetch })}
        >
            {children}
        </Link>
    );
}

function SearchCaseInput() {
    const [value, setValue] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        // Reset custom validity when input changes
        if (inputRef.current) {
            inputRef.current.setCustomValidity('');
        }
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!value || isNaN(Number(value))) {
            if (inputRef.current) {
                inputRef.current.setCustomValidity('Invalid case ID');
                inputRef.current.reportValidity();
            }
            return;
        }

        setIsValidating(true);

        try {
            const response = await axios.get(`/cases/${value}/exists`);

            if (response.data.valid) {
                window.location.href = route('legacy.cases.show', { account: value });
            } else {
                if (inputRef.current) {
                    inputRef.current.setCustomValidity('Case does not exist');
                    inputRef.current.reportValidity();
                }
            }
        } catch {
            if (inputRef.current) {
                inputRef.current.setCustomValidity('Error validating case ID');
                inputRef.current.reportValidity();
            }
            toast.error('Error validating case ID');
        } finally {
            setIsValidating(false);
        }
    };

    return (
        <form className="flex items-center gap-0.5 pr-1" onSubmit={handleSubmit}>
            <input
                ref={inputRef}
                type="text"
                className="all-revert text-foreground m-0 rounded-[2px] border border-solid border-gray-500 p-[2px] py-[3px] text-[11px] dark:bg-neutral-800"
                size={11}
                value={value}
                onChange={onChange}
                disabled={isValidating}
            />
            <button
                type="submit"
                className="all-revert m-0 rounded-[3px] border-none bg-green-700 p-[5px] text-white hover:bg-green-800 disabled:opacity-50"
                disabled={isValidating}
            >
                <Search className="h-3 w-3" />
            </button>
        </form>
    );
}

export default function Nav() {
    const { auth, quote } = usePage<SharedData>().props;
    const { user } = auth;

    const showQuoteEnabled = useCallback(() => {
        const cookieValue =
            typeof document !== 'undefined'
                ? document.cookie
                      .split('; ')
                      .find((row) => row.startsWith('showQuote='))
                      ?.split('=')[1]
                : true;

        return cookieValue === undefined || cookieValue === 'true';
    }, []);

    const isAdmin = user.role === 'Admin' || user.role === 'Super Admin';
    const isSuperAdmin = user.role === 'Super Admin';

    const canViewManagers = isAdmin || ['Property Manager'].includes(user.role);
    const canViewAssociations = isAdmin || ['Association', 'Property Manager'].includes(user.role);
    const canViewReports = user.role !== 'Attorney';

    return (
        <nav className="sticky top-0 z-[1000] flex flex-col items-center border-b border-solid border-b-[#a50000] bg-[#e1e1e1] 2xl:block dark:bg-neutral-900 print:hidden">
            <div className="flex flex-col items-center justify-between p-1 2xl:flex-row">
                <div className="flex items-center justify-between gap-0.5 2xl:justify-normal">
                    <NavLink href={route('dashboard')} className="hover:bg-transparent" inertia>
                        <AppLogo className="!h-8 !w-auto" />
                    </NavLink>
                    {!isAdmin && (
                        <NavLink href={route('legacy.users.show', { user: user.username })} component="my-account">
                            My Account
                        </NavLink>
                    )}
                    {isAdmin && (
                        <>
                            <NavLink href={route('legacy.info.show')} component="info" inertia>
                                Info
                            </NavLink>
                            <NavLink href="/attorneys" component="attorneys">
                                Attorneys
                            </NavLink>
                        </>
                    )}
                    {canViewManagers && (
                        <NavLink href={route('legacy.property-managers.index')} component="managers">
                            Managers
                        </NavLink>
                    )}
                    {canViewAssociations && (
                        <NavLink href={route('legacy.associations.index')} component="associations">
                            Associations
                        </NavLink>
                    )}
                    {isAdmin ? (
                        <NavLink href={route('search.index')} component="cases" inertia>
                            Cases
                        </NavLink>
                    ) : (
                        <NavLink href={route('legacy.cases.index')} component="cases">
                            Cases
                        </NavLink>
                    )}
                    {isAdmin && (
                        <NavLink href={route('legacy.tasks.index', { user: user.username })} component="tasks" inertia>
                            Tasks
                        </NavLink>
                    )}
                    {canViewReports && (
                        <NavLink href="/reports" component="reports">
                            Reports
                        </NavLink>
                    )}
                    <NavLink href="/subscriptions" component="subscriptions">
                        Subscriptions
                    </NavLink>
                </div>

                <div className="flex items-center justify-between gap-0.5 2xl:justify-normal">
                    {isAdmin && (
                        <>
                            <NavLink href="/stats">Stats</NavLink>
                            <NavLink href="/county-state">County/State</NavLink>
                            <NavLink href="/notary">Notary Select</NavLink>
                            <NavLink href="/upload_documents">Upload</NavLink>
                            <NavLink href="/users">Users</NavLink>
                            {isSuperAdmin && (
                                <NavLink href={route('admin.index')} component="admin" inertia>
                                    Sup Admin
                                </NavLink>
                            )}
                            <NavLink href="/templates">Templates</NavLink>
                            <NavLink href="/process_steps.php">Process</NavLink>
                        </>
                    )}
                    <NavLink href="/logout">Logout</NavLink>
                    {isAdmin && <SearchCaseInput />}
                </div>
            </div>
            {showQuoteEnabled() && isAdmin && ![24, 85, 536].includes(user.id) && (
                <div className="text-center">
                    <span className="font-bold text-green-700 dark:text-green-400">{quote}</span>
                </div>
            )}
        </nav>
    );
}
